import React, { useState, useCallback, useRef, useEffect } from "react";
import { useMsal } from "@azure/msal-react";
import { useSearchParams } from "react-router-dom";
import {
  trackEvent,
  trackException,
  trackTrace,
  trackMetric,
  trackPageView,
  flushTelemetry,
  SeverityLevel,
} from "./telemetry/telemetryService";

import {
  API_BASE_URL,
  API_SCOPE,
  TOPIC_SUBTOPIC_MAP,
  TOPIC_OPTIONS,
  ALLOWED_EXTENSIONS,
  ACCEPT_STRING,
  MAX_FILES,
  MAX_FILE_SIZE_BYTES,
} from "./config/ticketConfig";
import { resolveEarningsWorkspace, getFileExtension, formatFileSize } from "./utils/ticketUtils";
import type { AttachmentItem, SkippedFile, TicketSuccessResponse, TicketErrorResponse } from "./types/ticket";
import "./AskHrTicket.css";

const AskHrTicket: React.FC = () => {
  const { instance, accounts } = useMsal();
  const [searchParams] = useSearchParams();
  const userName = accounts[0]?.name || accounts[0]?.username || "User";

  // ─── Decode base64 "data" query param (sent by backend redirect) ─
  const base64Data: Record<string, string> | null = (() => {
    const dataParam = searchParams.get("data");
    if (!dataParam) return null;
    try {
      const json = atob(decodeURIComponent(dataParam));
      const parsed = JSON.parse(json);
      // Flatten to Record<string, string>, keeping only string values
      const result: Record<string, string> = {};
      for (const [k, v] of Object.entries(parsed)) {
        if (typeof v === "string" && v) result[k] = v;
      }
      return Object.keys(result).length > 0 ? result : null;
    } catch {
      return null;
    }
  })();

  // ─── Helper: lookup a value from base64 data first, then query params ─
  const getParam = useCallback((name: string, ...altKeys: string[]): string => {
    const keysToTry = [name, ...altKeys];
    const lowerKeysToTry = keysToTry.map((k) => k.toLowerCase());

    // 1. Check base64-decoded data first
    if (base64Data) {
      for (const key of keysToTry) {
        if (base64Data[key]) return base64Data[key];
      }
    }

    // 2. Fall back to direct query params (exact match first)
    for (const key of keysToTry) {
      const exact = searchParams.get(key);
      if (exact) return exact;
    }

    // 3. Case-insensitive fallback across all keys (name + altKeys)
    let result = "";
    searchParams.forEach((value, key) => {
      if (!result && lowerKeysToTry.includes(key.toLowerCase())) result = value;
    });
    return result;
  }, [base64Data, searchParams]);

  // ─── Pre-populate from query params or base64 data ───────────
  const [topic, setTopic] = useState(() => {
    const raw = getParam("topic", "EarningsTopic");
    if (!raw) return "";
    return TOPIC_OPTIONS.find((t) => t.toLowerCase() === raw.toLowerCase()) || "";
  });
  const [subtopic, setSubtopic] = useState(() => {
    const raw = getParam("subtopic", "EarningsSubtopic");
    if (!raw) return "";
    // Validate against the resolved topic's subtopics
    const topicRaw = getParam("topic", "EarningsTopic");
    const matchedTopic = TOPIC_OPTIONS.find((t) => t.toLowerCase() === topicRaw.toLowerCase());
    if (matchedTopic) {
      const subs = TOPIC_SUBTOPIC_MAP[matchedTopic] || [];
      return subs.find((s) => s.toLowerCase() === raw.toLowerCase()) || "";
    }
    return "";
  });
  const [subject, setSubject] = useState(() => getParam("shortDescription", "ShortDescription"));
  const [description, setDescription] = useState(() => getParam("description", "Description"));
  const [justification, setJustification] = useState(() => getParam("justification"));
  const [aliases, setAliases] = useState(() => getParam("aliases"));

  const [attachments, setAttachments] = useState<AttachmentItem[]>([]);
  const [rejectedFiles, setRejectedFiles] = useState<string[]>([]);

  // ─── WizardCorrelationId: ties this page back to the earnings wizard flow ─
  const wizardCorrelationId = getParam("WizardCorrelationId");
  const [isDragging, setIsDragging] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [caseNumber, setCaseNumber] = useState<string | null>(null);
  const [apiMessage, setApiMessage] = useState<string | null>(null);
  const [attachedFiles, setAttachedFiles] = useState<string[]>([]);
  const [skippedFiles, setSkippedFiles] = useState<SkippedFile[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragCounterRef = useRef(0);

  // ─── Telemetry: track page view on mount ─────────────────────
  useEffect(() => {
    trackPageView("AskHrTicket", window.location.pathname + window.location.search);
    trackEvent("AskHrTicketPageLoaded", {
      hasBase64Data: base64Data ? "true" : "false",
      prePopulatedTopic: topic || "none",
      prePopulatedSubtopic: subtopic || "none",
      hasSubject: subject ? "true" : "false",
      hasDescription: description ? "true" : "false",
      wizardCorrelationId: wizardCorrelationId || "none",
      userName,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addFiles = useCallback((newFiles: FileList | File[]) => {
    const all = Array.from(newFiles);
    const reasons: string[] = [];

    // 1. Check if adding more files would exceed the max
    const slotsAvailable = MAX_FILES - attachments.length;
    if (slotsAvailable <= 0) {
      setRejectedFiles([`Maximum ${MAX_FILES} files allowed. Remove a file before adding more.`]);
      setTimeout(() => setRejectedFiles([]), 5000);
      return;
    }

    const toProcess = all.slice(0, slotsAvailable);
    if (all.length > slotsAvailable) {
      reasons.push(`Only ${slotsAvailable} more file${slotsAvailable === 1 ? "" : "s"} can be added (max ${MAX_FILES} total). ${all.length - slotsAvailable} file${all.length - slotsAvailable === 1 ? " was" : "s were"} skipped.`);
    }

    const accepted: File[] = [];
    for (const file of toProcess) {
      const ext = getFileExtension(file.name);
      if (!ALLOWED_EXTENSIONS.has(ext)) {
        reasons.push(`${file.name} — unsupported file type (.${ext})`);
      } else if (file.size > MAX_FILE_SIZE_BYTES) {
        reasons.push(`${file.name} — exceeds 10 MB limit (${formatFileSize(file.size)})`);
      } else {
        accepted.push(file);
      }
    }

    if (reasons.length > 0) {
      setRejectedFiles(reasons);
      setTimeout(() => setRejectedFiles([]), 6000);
      trackEvent("FileAttachmentRejected", {
        rejectedCount: reasons.length.toString(),
        reasons: reasons.join(" | "),
      });
    }
    if (accepted.length === 0) return;

    trackEvent("FilesAttached", {
      count: accepted.length.toString(),
      totalSizeBytes: accepted.reduce((sum, f) => sum + f.size, 0).toString(),
      fileTypes: accepted.map((f) => getFileExtension(f.name)).join(","),
    });

    setAttachments((prev) => [
      ...prev,
      ...accepted.map((file) => ({
        id: `${file.name}-${file.size}-${Date.now()}-${Math.random()}`,
        file,
      })),
    ]);
  }, [attachments.length]);

  const removeAttachment = useCallback((id: string) => {
    setAttachments((prev) => prev.filter((a) => a.id !== id));
    trackEvent("FileAttachmentRemoved");
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setIsSubmitting(true);
      setSubmitSuccess(false);
      setSubmitError(null);
      setCaseNumber(null);
      setApiMessage(null);
      setAttachedFiles([]);
      setSkippedFiles([]);

      let capturedCorrelationId = "";

      const submitStartTime = performance.now();
      trackEvent("TicketSubmitStarted", {
        topic,
        subtopic,
        hasJustification: justification ? "true" : "false",
        hasAliases: aliases ? "true" : "false",
        attachmentCount: attachments.length.toString(),
        wizardCorrelationId: wizardCorrelationId || "none",
      });

      try {
        // 1. Acquire an access token silently for the backend API
        const account = accounts[0];
        if (!account) {
          throw new Error("No authenticated account found. Please sign in again.");
        }

        let accessToken: string;
        try {
          const tokenResponse = await instance.acquireTokenSilent({
            scopes: [API_SCOPE],
            account,
          });
          accessToken = tokenResponse.accessToken;
          trackEvent("TokenAcquiredSilently");
        } catch (tokenError) {
          // Silent acquisition can fail for many reasons in MSAL v4:
          // InteractionRequiredAuthError, BrowserAuthError (no cached token),
          // ClientAuthError (expired refresh token), etc.
          // Always fall back to interactive popup so the user can authenticate.
          const errorCode = tokenError instanceof Error ? (tokenError as { errorCode?: string }).errorCode || "" : "";
          trackTrace("Silent token acquisition failed, falling back to popup", SeverityLevel.Warning, {
            errorCode,
            errorType: tokenError instanceof Error ? tokenError.constructor.name : "unknown",
          });
          const tokenResponse = await instance.acquireTokenPopup({
            scopes: [API_SCOPE],
            account,
          });
          accessToken = tokenResponse.accessToken;
          trackEvent("TokenAcquiredViaPopup");
        }

        // 2. Build the requestPayload JSON per the HRD365 API spec
        // Resolve workspace from topic+subtopic selection (mirrors backend EarningsWizardHelper.ResolveEarningsWorkspace)
        // Falls back to base64/query param values if topic+subtopic don't resolve
        const resolvedWorkspace = resolveEarningsWorkspace(topic, subtopic)
          || getParam("ResolvedWorkspace")
          || getParam("Workspace");
        const watchListEmails = aliases
          .split(/[,;]/)
          .map((a) => a.trim())
          .filter((a) => a.length > 0);

        const requestPayload = {
          RequestSource: "MSX",
          IncomingObject: {
            ShortDescription: subject,
            Description: description,
            Workspace: resolvedWorkspace || "",
            UrgentJustification: justification || "",
            WatchListEmails: watchListEmails,
            UserAlias: account.username?.split("@")[0] || "",
          },
          Context: {
            UPN: account.username || "",
            OID: account.localAccountId || "",
            RequestSource: "MSX",
          },
        };

        // 3. Build multipart/form-data — do NOT set Content-Type manually; browser adds boundary
        const formData = new FormData();
        formData.append("requestPayload", JSON.stringify(requestPayload));

        // Add file attachments (max per API spec)
        for (const attachment of attachments.slice(0, MAX_FILES)) {
          formData.append("files", attachment.file, attachment.file.name);
        }

        // 4. POST to our Azure Function proxy endpoint
        const response = await fetch(
          `${API_BASE_URL}/SubmitSupportTicketWithAttachment`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "X-Wizard-Correlation-Id": wizardCorrelationId || "",
            },
            body: formData,
          }
        );

        const responseBody = await response.text();

        if (!response.ok) {
          // Parse structured error per API spec v2 §4.2
          let errorMessage = "Failed to submit support ticket.";
          let errorCorrelationId = "";
          try {
            const errorJson: TicketErrorResponse = JSON.parse(responseBody);
            errorMessage = errorJson.Message || errorMessage;
            errorCorrelationId = errorJson.CorrelationId || "";
          } catch {
            // Response is not JSON (e.g. 413 from server); use status text
            errorMessage = `Server error: ${response.status} ${response.statusText}`;
          }

          // Capture correlation ID for telemetry only (not shown to user)
          capturedCorrelationId = errorCorrelationId;

          trackException(
            new Error(errorMessage),
            SeverityLevel.Error,
            {
              event: "TicketSubmitApiError",
              statusCode: response.status.toString(),
              statusText: response.statusText,
              correlationId: errorCorrelationId,
              wizardCorrelationId: wizardCorrelationId || "",
              topic,
              subtopic,
            },
          );

          // Show a generic user-facing message for all API failures;
          // detailed error info is already captured in telemetry above.
          throw new Error("We couldn't submit your request. Please try again.");
        }

        // 5. Parse success response per API spec v2 §4.1:
        //    { caseNumber, message, attachments, skippedFiles }
        const result: TicketSuccessResponse = JSON.parse(responseBody);
        setCaseNumber(result.caseNumber ?? null);
        setApiMessage(result.message ?? null);
        setAttachedFiles(Array.isArray(result.attachments) ? result.attachments : []);

        const submitDuration = performance.now() - submitStartTime;
        trackMetric("TicketSubmitDurationMs", submitDuration, { topic, subtopic });

        if (Array.isArray(result.skippedFiles) && result.skippedFiles.length > 0) {
          setSkippedFiles(result.skippedFiles);
          trackEvent("TicketSubmitSkippedFiles", {
            count: result.skippedFiles.length.toString(),
            files: result.skippedFiles.map((sf) => sf.name).join(","),
          });
        }

        if (!result.caseNumber) {
          // AskHR returned no case number — partial failure
          trackTrace("Ticket submitted but no case number returned", SeverityLevel.Warning, {
            event: "TicketSubmitPartialFailure",
            message: result.message || "",
          });
          setSubmitError("We couldn't submit your request. Please try again.");
        } else {
          trackEvent("TicketSubmitSuccess", {
            caseNumber: result.caseNumber,
            topic,
            subtopic,
            attachmentCount: attachments.length.toString(),
            attachedCount: (result.attachments?.length ?? 0).toString(),
            skippedCount: (result.skippedFiles?.length ?? 0).toString(),
            wizardCorrelationId: wizardCorrelationId || "",
            durationMs: submitDuration.toFixed(0),
          });
          setSubmitSuccess(true);
        }
      } catch (err: unknown) {
        const errorMessage = err instanceof Error ? err.message : "An unexpected error occurred.";
        trackException(
          err instanceof Error ? err : new Error(errorMessage),
          SeverityLevel.Error,
          { event: "TicketSubmitException", topic, subtopic, wizardCorrelationId: wizardCorrelationId || "", correlationId: capturedCorrelationId },
        );
        // Show a generic message for all failures; API details stay in telemetry only
        setSubmitError("We couldn't submit your request. Please try again.");
      } finally {
        setIsSubmitting(false);
        flushTelemetry();
      }
    },
    [accounts, instance, topic, subtopic, subject, description, justification, aliases, attachments, getParam, wizardCorrelationId]
  );

  const isFormValid = topic.trim() !== "" && subtopic.trim() !== "" && subject.trim() !== "" && description.trim() !== "";

  return (
    <div className="ticket-page">
      {/* Header */}
      <header className="ticket-header">
        <div className="header-left">
          <svg className="ms-logo" viewBox="0 0 23 23" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
            <rect x="1" y="1" width="10" height="10" fill="#f25022" />
            <rect x="12" y="1" width="10" height="10" fill="#7fba00" />
            <rect x="1" y="12" width="10" height="10" fill="#00a4ef" />
            <rect x="12" y="12" width="10" height="10" fill="#ffb900" />
          </svg>
          <h1 className="app-title">MSX Support Ticket Details</h1>
        </div>
        <div className="header-right">
          <div className="user-avatar" aria-hidden="true">{userName.charAt(0).toUpperCase()}</div>
          <span className="user-name">{userName}</span>
        </div>
      </header>

      <main className="ticket-main">
        <div className="page-content">
          {/* Page title */}
          <div className="section-header">
            <h2 className="section-title" id="askhr-form-title">Complete your support ticket</h2>
            <p className="section-description">
              Review the details below and optionally add attachments before final submission. Fields marked with <span className="askhr-required-star" aria-hidden="true">*</span> are required.
            </p>
          </div>

          {submitSuccess ? (
            <div className="askhr-result-container">
              {/* ── Success banner ── */}
              <div className="askhr-success-banner" role="status">
                <svg viewBox="0 0 20 20" fill="currentColor" width="22" height="22" aria-hidden="true" focusable="false">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                </svg>
                <div>
                  <strong>Ticket submitted successfully!</strong>
                  {caseNumber && (
                    <span className="askhr-case-number">Case Number: <strong>{caseNumber}</strong></span>
                  )}
                  <span>{apiMessage || "Your support request has been created. You will receive a confirmation shortly."}</span>
                </div>
              </div>

              {/* ── Successfully attached files ── */}
              {attachedFiles.length > 0 && (
                <div className="askhr-attached-files">
                  <strong>Attached files ({attachedFiles.length}):</strong>
                  <ul className="askhr-attached-list">
                    {attachedFiles.map((fileName, idx) => (
                      <li key={idx} className="askhr-attached-item">
                        <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14" aria-hidden="true" focusable="false">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 00-1.414 0L8 12.586 4.707 9.293a1 1 0 00-1.414 1.414l4 4a1 1 0 001.414 0l8-8a1 1 0 000-1.414z" clipRule="evenodd" />
                        </svg>
                        {fileName}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* ── Skipped files warning ── */}
              {skippedFiles.length > 0 && (
                <div className="askhr-skipped-files">
                  <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16" aria-hidden="true" focusable="false">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <strong>Some files were not attached:</strong>
                    <ul className="askhr-skipped-list">
                      {skippedFiles.map((sf, idx) => (
                        <li key={idx}>{sf.name} — {sf.reason}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              <p className="askhr-close-tab-message" role="status" aria-live="polite">
                <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16" aria-hidden="true" focusable="false">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                You can close this tab now.
              </p>
            </div>
          ) : (
            <form className="askhr-form" onSubmit={handleSubmit} aria-labelledby="askhr-form-title">
              {/* Error banner */}
              {submitError && (
                <div className="askhr-error-banner" role="alert">
                  <svg viewBox="0 0 20 20" fill="currentColor" width="18" height="18" className="askhr-error-icon" aria-hidden="true" focusable="false">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <strong>Submission failed</strong>
                    <p className="askhr-error-text">{submitError}</p>
                  </div>
                </div>
              )}
              {/* Topic */}
              <div className="askhr-field">
                <label className="askhr-label" htmlFor="askhr-topic">
                  Please select the topic <span className="askhr-required-star" aria-hidden="true">*</span>
                </label>
                <select
                  id="askhr-topic"
                  className="askhr-select"
                  value={topic}
                  onChange={(e) => { setTopic(e.target.value); setSubtopic(""); }}
                  required
                >
                  <option value="" disabled>Select a topic…</option>
                  {TOPIC_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>

              {/* Subtopic */}
              <div className="askhr-field">
                <label className="askhr-label" htmlFor="askhr-subtopic">
                  Please select the subtopic <span className="askhr-required-star" aria-hidden="true">*</span>
                </label>
                <select
                  id="askhr-subtopic"
                  className="askhr-select"
                  value={subtopic}
                  onChange={(e) => setSubtopic(e.target.value)}
                  required
                  disabled={!topic}
                >
                  <option value="" disabled>Select a subtopic…</option>
                  {(TOPIC_SUBTOPIC_MAP[topic] || []).map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>

              {/* Subject */}
              <div className="askhr-field">
                <label className="askhr-label" htmlFor="askhr-subject">
                  What is the subject of your request? (Limit 160 characters) <span className="askhr-required-star" aria-hidden="true">*</span>
                </label>
                <textarea
                  id="askhr-subject"
                  className="askhr-textarea askhr-textarea--short"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value.slice(0, 160))}
                  maxLength={160}
                  required
                  placeholder="Brief subject for your request"
                  aria-describedby="askhr-subject-charcount"
                />
                <span className="askhr-char-count" id="askhr-subject-charcount">{subject.length}/160</span>
              </div>

              {/* Description */}
              <div className="askhr-field">
                <label className="askhr-label" htmlFor="askhr-description">
                  In more detail, please describe your issue <span className="askhr-required-star" aria-hidden="true">*</span>
                </label>
                <textarea
                  id="askhr-description"
                  className="askhr-textarea"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  required
                  placeholder="Provide as much detail as possible"
                  rows={4}
                />
              </div>

              {/* Business Justification */}
              <div className="askhr-field">
                <label className="askhr-label" htmlFor="askhr-justification">
                  If urgent, provide business justification
                </label>
                <textarea
                  id="askhr-justification"
                  className="askhr-textarea"
                  value={justification}
                  onChange={(e) => setJustification(e.target.value)}
                  placeholder="Provide business justification"
                  rows={3}
                />
              </div>

              {/* Aliases */}
              <div className="askhr-field">
                <label className="askhr-label" htmlFor="askhr-aliases">
                  Alias of secondary/back-up contacts/other users/groups to receive all incident emails (Separate each user/group with a comma or semicolon)
                </label>
                <input
                  id="askhr-aliases"
                  type="text"
                  className="askhr-input"
                  value={aliases}
                  onChange={(e) => setAliases(e.target.value)}
                  placeholder="Alias of the user or group"
                />
              </div>

              {/* Attachments */}
              <div className="askhr-field">
                <label className="askhr-label">Attachments</label>
                <p className="askhr-field-hint" id="askhr-attachment-hint">Add screenshots, logs, or documents that help explain the issue.</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept={ACCEPT_STRING}
                  tabIndex={-1}
                  aria-hidden="true"
                  onChange={(e) => {
                    if (e.target.files && e.target.files.length > 0) {
                      addFiles(e.target.files);
                      e.target.value = "";
                    }
                  }}
                  className="file-input-hidden"
                />
                <div
                  className={`askhr-dropzone${attachments.length >= MAX_FILES ? " askhr-dropzone--disabled" : ""}${isDragging ? " askhr-dropzone--dragover" : ""}`}
                  role="button"
                  tabIndex={attachments.length >= MAX_FILES ? -1 : 0}
                  aria-label={attachments.length >= MAX_FILES ? `Maximum ${MAX_FILES} files reached` : `Upload file attachments. Drag and drop or press Enter to browse. Maximum ${MAX_FILES} files, 10 MB each.`}
                  aria-disabled={attachments.length >= MAX_FILES}
                  aria-describedby="askhr-attachment-hint"
                  onClick={() => {
                    if (attachments.length < MAX_FILES) fileInputRef.current?.click();
                  }}
                  onKeyDown={(e) => {
                    if ((e.key === 'Enter' || e.key === ' ') && attachments.length < MAX_FILES) {
                      e.preventDefault();
                      fileInputRef.current?.click();
                    }
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onDragEnter={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    dragCounterRef.current++;
                    if (dragCounterRef.current === 1 && attachments.length < MAX_FILES) {
                      setIsDragging(true);
                    }
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    dragCounterRef.current--;
                    if (dragCounterRef.current === 0) {
                      setIsDragging(false);
                    }
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    dragCounterRef.current = 0;
                    setIsDragging(false);
                    if (e.dataTransfer.files.length > 0) {
                      addFiles(e.dataTransfer.files);
                    }
                  }}
                >
                  <svg className="askhr-dropzone-icon" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
                    <path d="M24 32V16m0 0l-8 8m8-8l8 8" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M8 32v4a4 4 0 004 4h24a4 4 0 004-4v-4" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <div className="askhr-dropzone-content">
                    <span className="askhr-dropzone-text">
                      {attachments.length >= MAX_FILES
                        ? `Maximum ${MAX_FILES} files reached`
                        : isDragging
                          ? "Drop your files here"
                          : <>Drag & drop files here, or <span className="askhr-dropzone-link">click to browse</span></>}
                    </span>
                    <span className="askhr-dropzone-hint">
                      Max {MAX_FILES} files · 10 MB each · .png .jpg .pdf .docx .pptx .txt .md .json .gif .bmp
                    </span>
                  </div>
                </div>

                {rejectedFiles.length > 0 && (
                  <div className="rejected-message" role="alert" style={{ marginTop: 8 }}>
                    {rejectedFiles.map((msg, idx) => (
                      <div key={idx}>{msg}</div>
                    ))}
                  </div>
                )}

                {attachments.length > 0 && (
                  <div className="askhr-file-list">
                    <span className="askhr-file-count">{attachments.length}/{MAX_FILES} files</span>
                    {attachments.map((a) => (
                      <div key={a.id} className="askhr-file-item">
                        <span className="askhr-file-name">{a.file.name}</span>
                        <span className="askhr-file-size">{formatFileSize(a.file.size)}</span>
                        <button
                          type="button"
                          className="askhr-file-remove"
                          onClick={() => removeAttachment(a.id)}
                          aria-label={`Remove file ${a.file.name}`}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Submit */}
              <div className="askhr-submit-row">
                <button
                  type="submit"
                  className="submit-btn"
                  disabled={isSubmitting || !isFormValid}
                  aria-busy={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner" aria-hidden="true" /> Submitting…
                    </>
                  ) : (
                    "Submit"
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </main>
    </div>
  );
};

export default AskHrTicket;
